import {
  App,
  Button,
  Card,
  DatePicker,
  Dropdown,
  Flex,
  Form,
  type FormProps,
  Input,
  Result,
  theme,
  Typography,
} from 'antd';
import FormItem from 'antd/es/form/FormItem';
import { useMutation } from 'jsonapi-react';
import { ChevronDown, ExternalLink, FilePen, Inbox, Printer } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { catchError, EMPTY, from, tap } from 'rxjs';
import PrepSheetFilterFields, { getExclusiveIdsInTree } from './prep-sheet-filter-fields.component';
import LoadingSpin from '../../../components/loading-spin';
import SyncSuspense from '../../../components/sync-suspense';
import {
  type MealCategory,
  PrepSheetConfiguration,
  type Route,
  type Site,
} from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import useSecuredApi from '../../../hooks/use-secured-api';
import { type DisplayConfig } from '../../../types';
import { LocalDate } from '../../../utils/local-date';

enum SubmitMethod {
  GENERATE,
  SAVE,
  GENERATE_AND_SAVE,
}
interface FiltersType {
  routes?: Array<Route['id']>;
  orderDate?: LocalDate;
  site?: Array<Exclude<Site['id'], undefined>>;
  mealCategory?: Array<Exclude<MealCategory['id'], undefined>>;
  mainType?: string[];
}

interface FormFieldsType {
  filters: FiltersType;
  enabledDisplayConfig: Array<keyof DisplayConfig>;
  generate: boolean;
  save: boolean;
  name?: string;
}

const PrepSheetsPage = (): ReactNode => {
  const { modal } = App.useApp();
  const api = useSecuredApi();
  const notification = useNotification();
  const [formInstance] = Form.useForm();
  const { token } = theme.useToken();
  const [reportUrl, setReportUrl] = useState<string | undefined>('');
  const pdfSection = useRef<HTMLDivElement>(null);
  const [addPrepSheetConfigurations] = useMutation<PrepSheetConfiguration>([
    'prep-sheet-configuration',
  ]);
  const enabledDisplayConfigWatch = Form.useWatch('enabledDisplayConfig', formInstance);
  const submitMethodRef = useRef(SubmitMethod.GENERATE);
  const saveNameRef = useRef('');

  const printSheet = (): void => {
    const hiddenFrame = document.createElement('iframe');
    hiddenFrame.onload = (): void => {
      const closePrint = (): void => {
        document.body.removeChild(hiddenFrame);
      };
      hiddenFrame.contentWindow!.onbeforeunload = closePrint;
      hiddenFrame.contentWindow!.onafterprint = closePrint;
      hiddenFrame.contentWindow!.print();
    };
    hiddenFrame.style.display = 'none'; // hide iframe
    hiddenFrame.src = reportUrl ?? '';
    document.body.appendChild(hiddenFrame);
  };

  const openInNewWindow = (): void => {
    window.open(reportUrl);
  };

  const fetchSheetFromTableExport = (
    serviceDate: LocalDate,
    sitesIds: string[],
    mealCategoryIds: string[],
    mainTypes: string[],
    displayConfig: string[]
  ): void => {
    from(
      api.post(
        process.env.REACT_APP_PREP_SHEET_ENDPOINT!,
        { serviceDate, siteIds: sitesIds, mealCategoryIds, mainTypes, displayConfig },
        {},
        {},
        true
      )
    )
      .pipe(
        tap(({ blob }) => {
          const url = URL.createObjectURL(blob);
          setReportUrl(url);

          pdfSection.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }),
        catchError((error: Error) => {
          setReportUrl('');
          const [description, title] = error?.message.split('\n');
          notification.error({
            description: description ?? 'No sheets available',
            message: title ?? '',
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  const fetchSheet: FormProps<FormFieldsType>['onFinish'] = (submitted): void => {
    setReportUrl(undefined);
    const sitesIds = getExclusiveIdsInTree(submitted.filters.site ?? []);

    if (
      submitMethodRef.current === SubmitMethod.GENERATE ||
      submitMethodRef.current === SubmitMethod.GENERATE_AND_SAVE
    ) {
      fetchSheetFromTableExport(
        submitted.filters.orderDate!,
        sitesIds ?? [],
        submitted.filters.mealCategory ?? [],
        submitted.filters.mainType ?? [],
        submitted.enabledDisplayConfig
      );
    }

    if (
      submitMethodRef.current === SubmitMethod.SAVE ||
      submitMethodRef.current === SubmitMethod.GENERATE_AND_SAVE
    ) {
      const prepSheetConfiguration = new PrepSheetConfiguration(
        saveNameRef.current,
        submitted.enabledDisplayConfig?.length ? submitted.enabledDisplayConfig : undefined,
        sitesIds.length ? sitesIds : undefined,
        submitted.filters.mealCategory?.length ? submitted.filters.mealCategory : undefined,
        submitted.filters.mainType?.length ? submitted.filters.mainType : undefined
      );
      from(addPrepSheetConfigurations(prepSheetConfiguration))
        .pipe(
          tap(() => {
            saveNameRef.current = '';
            if (submitMethodRef.current === SubmitMethod.SAVE) {
              setReportUrl('');
            }
          }),
          tap(() => {
            notification.success({
              description: 'Preparation sheet configuration saved successfully',
              message: '',
            });
          }),
          catchError((error: Error) => {
            setReportUrl('');
            const [description, title] = error?.message.split('\n');
            notification.error({
              description: description ?? 'No sheets available',
              message: title ?? '',
            });
            return EMPTY;
          })
        )
        .subscribe();
    }
  };

  const promptConfigurationName = (): void => {
    void modal.confirm({
      width: '50%',
      title: (
        <Typography.Title level={4} style={{ marginBlockStart: '0.5em' }}>
          Provide configuration name
        </Typography.Title>
      ),
      icon: <FilePen style={{ width: '1.6em', height: '1.6em', margin: '0.8em' }} />,
      content: (
        <Form
          id="prep-sheet-configuration-name-form"
          requiredMark={false}
          layout="vertical"
          colon={false}
          onFinish={({ name }) => {
            saveNameRef.current = name;
            formInstance.submit();
          }}
        >
          <FormItem label="Name" name="name" required>
            <Input />
          </FormItem>
        </Form>
      ),
      okText: 'Submit',
      cancelText: 'Cancel',
      okButtonProps: {
        form: 'prep-sheet-configuration-name-form',
        htmlType: 'submit',
        type: 'primary',
      },
    });
  };

  return (
    <Flex gap="middle" vertical>
      <Flex gap="middle" style={{ width: '100%' }}>
        <Flex flex="1" vertical>
          <Card styles={{ body: { padding: `${token.paddingSM}px ${token.paddingXS}px` } }}>
            <Flex gap="small" vertical>
              <Flex
                style={{ paddingInline: token.paddingMD, paddingBlockStart: token.paddingSM }}
                vertical
              >
                <Typography.Title level={3} style={{ margin: 0 }}>
                  Preparation Sheets
                </Typography.Title>
                <Typography.Text
                  style={{ marginBlockStart: token.marginXS, cursor: 'initial' }}
                  disabled
                >
                  Generate and export preparation sheets for kitchen
                </Typography.Text>
              </Flex>
              <Form
                form={formInstance}
                layout="vertical"
                onFinish={fetchSheet}
                initialValues={{
                  filters: { orderDate: new LocalDate() },
                  enabledDisplayConfig: ['showSummary'],
                  generate: true,
                  save: false,
                }}
                style={{ paddingInline: token.paddingMD, marginBlockStart: token.marginMD }}
              >
                <Form.Item<FormFieldsType> name="name" hidden={true}>
                  <input />
                </Form.Item>

                <Form.Item<FormFieldsType>
                  name={['filters', 'orderDate']}
                  label="Order date"
                  getValueProps={(localDate) => ({ value: localDate.toDayjs() })}
                  normalize={(value) => new LocalDate(value)}
                >
                  <DatePicker allowClear={false} />
                </Form.Item>

                <PrepSheetFilterFields />

                <Form.Item<FormFieldsType>>
                  <Flex
                    gap="small"
                    style={{
                      flexDirection: 'row-reverse',
                      marginBlock: token.marginMD,
                    }}
                  >
                    <Dropdown.Button
                      menu={{
                        items: [
                          {
                            label: `Generate and save`,
                            key: '2',
                            disabled:
                              enabledDisplayConfigWatch === undefined ||
                              enabledDisplayConfigWatch.length === 0,
                            onClick: () => {
                              submitMethodRef.current = SubmitMethod.GENERATE_AND_SAVE;
                              promptConfigurationName();
                            },
                          },
                        ],
                        disabled:
                          enabledDisplayConfigWatch === undefined ||
                          enabledDisplayConfigWatch.length === 0 ||
                          reportUrl == null,
                      }}
                      icon={<ChevronDown />}
                      type="primary"
                      disabled={
                        enabledDisplayConfigWatch === undefined ||
                        enabledDisplayConfigWatch.length === 0
                      }
                      loading={reportUrl == null}
                      onClick={() => {
                        submitMethodRef.current = SubmitMethod.GENERATE;
                        formInstance.submit();
                      }}
                    >
                      Generate
                    </Dropdown.Button>
                    <Button
                      block
                      onClick={() => {
                        submitMethodRef.current = SubmitMethod.SAVE;
                        promptConfigurationName();
                      }}
                      disabled={
                        enabledDisplayConfigWatch === undefined ||
                        enabledDisplayConfigWatch.length === 0
                      }
                    >
                      Save Only
                    </Button>
                  </Flex>
                </Form.Item>
              </Form>
            </Flex>
          </Card>
        </Flex>

        <Flex ref={pdfSection} flex="1" style={{ maxWidth: 600 }} vertical>
          <Card
            styles={{
              body: reportUrl != null ? { padding: 0 } : undefined,
            }}
            actions={[
              <Flex
                key="actions"
                gap="small"
                style={{ flexDirection: 'row-reverse', paddingInline: token.paddingSM }}
              >
                <Button block icon={<Printer />} onClick={printSheet} disabled={!reportUrl?.length}>
                  Print
                </Button>
                <Button
                  block
                  icon={<ExternalLink />}
                  onClick={openInNewWindow}
                  disabled={!reportUrl?.length}
                >
                  Open in new window
                </Button>
              </Flex>,
            ]}
          >
            <SyncSuspense fallback={<LoadingSpin />} condition={reportUrl != null}>
              <Flex gap="small" justify="center" flex="1 1 100%" vertical>
                <SyncSuspense
                  fallback={
                    <Result
                      title={<Typography.Title level={4}>Nothing to preview</Typography.Title>}
                      icon={<Inbox />}
                      style={{ fontSize: '3em' }}
                    />
                  }
                  condition={!!reportUrl && reportUrl?.length}
                >
                  <iframe
                    src={reportUrl}
                    style={{ border: 0, height: 'auto', aspectRatio: '210 / 297' }}
                    width="100%"
                  />
                </SyncSuspense>
              </Flex>
            </SyncSuspense>
          </Card>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default PrepSheetsPage;
